import { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';

// TODO: Replace when an appropriate typing is made
export interface TextFrom911 {
  textFrom911: {
    data?: {
      call_start_time: number;
    };
  };
}

/**
 * Retrieves the call start time for a given call incident.
 *
 * If there are multiple locations (primary and secondary), it returns the most recent call start time.
 * If no locations are available, it will return the call start time from `textFrom911` if present.
 *
 */

export const getCallStartTime = (
  callIncident: EmergencyCall | TextFrom911,
): number => {
  let locationTime = 0;
  let secondaryLocationTime = 0;
  if ('core' in callIncident) {
    const { mostRecentLocation, mostRecentSecondaryLocation } =
      getMostRecentLocations(callIncident);
    if (mostRecentLocation) {
      if ('call_start_time' in mostRecentLocation) {
        locationTime = mostRecentLocation.call_start_time;
      } else {
        locationTime = mostRecentLocation.conversation_start_time;
      }
    }
    if (mostRecentSecondaryLocation) {
      secondaryLocationTime = mostRecentSecondaryLocation.call_start_time;
    }
    const latestTime = Math.max(locationTime, secondaryLocationTime);
    if (latestTime > 0) {
      return latestTime;
    }
  } else if (callIncident.textFrom911) {
    // Fallback to textFrom911 call_start_time if locations and secondaryLocations are empty
    return callIncident.textFrom911?.data?.call_start_time || 0;
  }
  return 0;
};

/**
 * Sorts an obj of call incidents by their respective call start times in descending order.
 * Uses `getCallStartTime` to retrieve the most recent call start time for each incident.
 * The sorted obj is to be used with Object.keys in parts of the code
 * Object.keys() does preserve the order of object properties
 * @returns - A sorted CallQueue with key value pairs
 */

export const sortCallQueueByCallStartTime = (
  callQueue: Record<string, EmergencyCall | TextFrom911>,
) => {
  return Object.fromEntries(
    Object.entries(callQueue).sort(([, callData_a], [, callData_b]) => {
      const timeA = getCallStartTime(callData_a);
      const timeB = getCallStartTime(callData_b);
      return timeB - timeA;
    }),
  );
};
